import { useEffect, useMemo, useRef } from "react"
import { notifyManager, useQueryClient } from "@tanstack/react-query"
import {
  isDateRangeWithinNormalPerformancePricingRefreshWindow,
} from "connected-components/dashboard/helpers/date-calculations"
import { getInstrumentSuffixForIntradayContributionPrefix } from "gatherers/contributors-data-integrator.utils"
import { isPinnedHistoricalBatch } from "gatherers/helpers"

function getGathererInvalidationKeys(gatherers, startDate, endDate) {
  const withinRefreshWindow = isDateRangeWithinNormalPerformancePricingRefreshWindow(startDate, endDate)
  const queryKeys = []

  for (const gatherer of gatherers) {
    if (!gatherer?.key) continue

    // Batch-trend gatherers key on one immutable, already-committed batchId — a new batch gets
    // its own new key that fetches on its own; invalidating an old one just re-fetches unchanged data.
    if (gatherer.skipInvalidation) continue

    const isContribution = getInstrumentSuffixForIntradayContributionPrefix(gatherer.prefix) != null
    if (!isContribution && !withinRefreshWindow) continue

    queryKeys.push(gatherer.graphqlKey ?? gatherer.key)
  }
  return queryKeys
}

function invalidateQueryKeys(queryClient, queryKeys) {
  notifyManager.batch(() => {
    for (const queryKey of queryKeys) {
      queryClient.invalidateQueries({ queryKey, exact: true, refetchType: "active" })
    }
  })
}

// The value seen on mount is the baseline: only a later, different value refetches.
export function useInvalidateQueryKeysOnChange(value, queryKeys) {
  const queryClient = useQueryClient()
  const previousValueRef = useRef(value)

  useEffect(() => {
    if (!value || value === previousValueRef.current) return

    previousValueRef.current = value
    invalidateQueryKeys(queryClient, queryKeys)
  }, [value, queryClient, queryKeys])
}

/**
 * Invalidates TanStack Query cache entries for active gatherers when either the committed-batch
 * `asOf` timestamp advances, or, absent a committed batch, when the live-price `intradayPricingAsOf`
 * clock advances. `intradayPricingAsOf` is a dashboard-wide clock (Redux `core` slice, written from
 * a `timestampUpdated` socket event in dashboard.jsx) — not a per-portfolio price signal.
 *
 * Eligibility rules:
 *  - Batch `asOf` trigger: skipped once `intradayBatchMetadata.id` is a genuine historical pin
 *    (diverges from `lastIntradayBatchId`) — a pinned batch's own asOf shift refetches via its key change.
 *  - Live-price `intradayPricingAsOf` trigger: fires on every advance, including while a batch is pinned,
 *    rolling the pinned batch forward onto the latest snapshot (op-hops reprices on refetch). This keeps
 *    intraday-instrument metrics live when a newer batch has arrived but hasn't been adopted yet.
 *  - `intradayContribution*` gatherers: always invalidated (fixed short periods, ungated).
 *  - All other gatherers: only invalidated when the date range is ≤ 1 month.
 */
export function useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate, intradayPricingAsOf, lastIntradayBatchId) {
  const queryClient = useQueryClient()
  const prevAsOfRef = useRef(intradayBatchMetadata?.asOf ?? null)
  const invalidationKeys = useMemo(() => getGathererInvalidationKeys(gatherers, startDate, endDate), [gatherers, startDate, endDate])

  useEffect(() => {
    const asOf = intradayBatchMetadata?.asOf
    if (!asOf || asOf === prevAsOfRef.current || isPinnedHistoricalBatch(intradayBatchMetadata?.id, lastIntradayBatchId)) return

    prevAsOfRef.current = asOf
    invalidateQueryKeys(queryClient, invalidationKeys)
    // Full object dep: re-runs whenever .asOf or .id changes — both are read here
    // (.asOf directly, .id via isPinnedHistoricalBatch).
  }, [intradayBatchMetadata, invalidationKeys, queryClient, lastIntradayBatchId])

  // On a coincident batch-commit + price-tick render (non-pinned), this and the batch-asOf
  // effect both invalidate the same gatherers. Harmless: TanStack coalesces concurrent
  // refetches of a key into one in-flight request, so it's a single refetch, not two.
  useInvalidateQueryKeysOnChange(intradayPricingAsOf, invalidationKeys)
}
